import { useState, useCallback, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Info } from 'lucide-react';
import type { TemplateDefinition, OutreachTemplate, OutreachItem } from '../../types/outreach';
import { DEFAULT_DEFINITION } from '../../constants/outreach';
import WizardStepIndicator from './wizard/WizardStepIndicator';
import WizardNavigation from './wizard/WizardNavigation';
import WizardStep0TemplateSelection from './wizard/WizardStep0TemplateSelection';
import WizardStep1BasicInfo from './wizard/WizardStep1BasicInfo';
import WizardStep2Questions from './wizard/WizardStep2Questions';
import WizardStep3Demographics from './wizard/WizardStep3Demographics';
import WizardStep4Settings from './wizard/WizardStep4Settings';
import WizardStep5Preview from './wizard/WizardStep5Preview';
import WizardStep6Launch from './wizard/WizardStep6Launch';
import OutreachReportWizard from './Analytics/reports/OutreachReportWizard';
import api from '../../lib/api';
import { EntityLink } from './RelatedEntitySelector';
import toast from 'react-hot-toast';
import Modal from '@shared/ui/Modal';
import Button from '@shared/ui/Button';

export interface OutreachWizardHandle {
  attemptClose: () => void;
}

interface Props {
  workspaceId: string;
  templates: OutreachTemplate[];
  onSave: (name: string, description: string, type: string, definition: TemplateDefinition, launch: boolean, dates?: { effective_start?: string; effective_end?: string }, itemId?: string) => Promise<{ itemId: string; publicUrl?: string } | undefined>;
  onClose: () => void;
  editingItem?: OutreachItem | null;
  hasResponses?: boolean;
}

const STEPS = [
  { number: 0, label: 'Template' },
  { number: 1, label: 'Basic Info' },
  { number: 2, label: 'Questions' },
  { number: 3, label: 'Demographics' },
  { number: 4, label: 'Settings' },
  { number: 5, label: 'Design & Preview' },
  { number: 6, label: 'Launch' }
];

const OutreachCreationWizard = forwardRef<OutreachWizardHandle, Props>(({
  workspaceId,
  templates,
  onSave,
  onClose,
  editingItem,
  hasResponses = false
}, ref) => {
  const isEditing = !!editingItem;
  const isLaunched = isEditing && (
    editingItem?.status === 'active' ||
    editingItem?.status === 'completed' ||
    !!editingItem?.launched_at
  );
  const settingsOnlyMode = isLaunched && hasResponses;
  const [currentStep, setCurrentStep] = useState(settingsOnlyMode ? 4 : (isEditing ? 1 : 0));
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  
  const [selectedTemplate, setSelectedTemplate] = useState<OutreachTemplate | null>(null);
  const [name, setName] = useState(editingItem?.name || '');
  const [description, setDescription] = useState(editingItem?.description || '');
  const [type, setType] = useState<'campaign' | 'survey' | 'poll' | 'message' | 'petition' | ''>(editingItem?.type || '');
  // Messages and petitions have no Questions step: a message collects nothing,
  // a petition's "response" is the demographics signature form
  const isQuestionless = type === 'message' || type === 'petition';
  const [definition, setDefinition] = useState<TemplateDefinition>(editingItem?.definition_json || DEFAULT_DEFINITION);
  const [entityLinks, setEntityLinks] = useState<EntityLink[]>(
    editingItem?.definition_json?.entity_links || []
  );
  
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launched, setLaunched] = useState(false);
  const [collectorUrl, setCollectorUrl] = useState<string>();
  const [createdItemId, setCreatedItemId] = useState<string>();
  const [showReportWizard, setShowReportWizard] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [lastSavedStep, setLastSavedStep] = useState<number>(-1);
  const [testLinkUrl, setTestLinkUrl] = useState<string>();
  const [creatingTestLink, setCreatingTestLink] = useState(false);
  
  // Branding state
  const [organizationLogoUrl, setOrganizationLogoUrl] = useState<string | null>(null);
  const [workspaceLogoUrl, setWorkspaceLogoUrl] = useState<string | null>(null);
  const orgBrandingRef = useRef<Record<string, any> | null>(null);

  type LogoPosition = 'header_left' | 'header_center' | 'header_right' | 'footer_left' | 'footer_center' | 'footer_right';

  const buildBrandingFromOrg = useCallback((
    orgBranding: Record<string, any>,
    orgLogoUrl: string | null,
    wsLogoUrl: string | null
  ): TemplateDefinition['branding'] => {
    const position: LogoPosition =
      orgBranding.logo_position === 'left' ? 'header_left' :
      orgBranding.logo_position === 'right' ? 'header_right' : 'header_center';

    return {
      logos: {
        organization: {
          enabled: orgBranding.show_organization_logo ?? true,
          url: orgLogoUrl,
          position
        },
        workspace: {
          enabled: false,
          url: wsLogoUrl,
          position: 'header_left' as LogoPosition
        }
      },
      colors: {
        primary: orgBranding.primary_color || '#2563eb',
        secondary: orgBranding.secondary_color || '#64748b',
        background: orgBranding.background_color || '#ffffff',
        text: orgBranding.text_color || '#1e293b'
      },
      fonts: {
        heading: orgBranding.heading_font || 'Inter',
        body: orgBranding.body_font || 'Inter'
      },
      layout_template: orgBranding.layout_template || 'modern',
      custom_header_html: orgBranding.custom_header_html || null,
      custom_footer_html: orgBranding.custom_footer_html || null
    };
  }, []);

  // Sync entity links with definition, filtering out empty placeholder entries
  useEffect(() => {
    const validLinks = entityLinks.filter(l => l.entity_type && l.entity_id);
    setDefinition(prev => ({
      ...prev,
      entity_links: validLinks
    }));
  }, [entityLinks]);
  
  // Fetch organization branding defaults and workspace/org data
  useEffect(() => {
    const fetchBrandingDefaults = async () => {
      if (!workspaceId) {
        console.warn('No workspaceId provided to OutreachCreationWizard');
        return;
      }

      try {
        const workspaceResponse = await api.get(`/workspaces/${workspaceId}`);
        const workspace = workspaceResponse.data?.data || workspaceResponse.data;
        
        if (!workspace) {
          console.error('No workspace data returned');
          return;
        }
        
        const orgId = workspace.owner_org_id;
        
        if (!orgId) {
          console.error('No owner_org_id found in workspace data:', workspace);
          return;
        }
        
        const wsLogoUrl = workspace.logoUrl || workspace.logo_url || null;
        setWorkspaceLogoUrl(wsLogoUrl);
        
        const orgResponse = await api.get(`/organizations/${orgId}`);
        const organization = orgResponse.data?.data || orgResponse.data;
        const orgLogoUrl = organization?.logoUrl || organization?.logo_url || null;
        setOrganizationLogoUrl(orgLogoUrl);
        
        const brandingResponse = await api.get(`/outreach-unified/organizations/${orgId}/branding`);
        const orgBranding = brandingResponse.data?.data;
        
        if (orgBranding) {
          orgBrandingRef.current = orgBranding;
          const branding = buildBrandingFromOrg(orgBranding, orgLogoUrl, wsLogoUrl);
          setDefinition(prev => ({
            ...prev,
            branding
          }));
        }
      } catch (error) {
        console.error('Failed to fetch branding defaults:', error);
      }
    };
    
    fetchBrandingDefaults();
  }, [workspaceId, buildBrandingFromOrg]);

  // Mark step as completed
  const markStepComplete = useCallback((step: number) => {
    setCompletedSteps(prev => new Set(prev).add(step));
  }, []);

  // Auto-complete Questions step for questionless types (not applicable)
  useEffect(() => {
    if (isQuestionless) {
      markStepComplete(2);
    }
  }, [isQuestionless, markStepComplete]);

  // Validate step
  const isStepValid = useCallback((step: number): boolean => {
    switch (step) {
      case 0: // Template selection - always valid (can skip)
        return true;
      case 1: // Basic Info
        return name.trim().length > 0 && type !== '';
      case 2: // Questions - not applicable for questionless types
        if (isQuestionless) return true;
        return definition.questions.length > 0 &&
               definition.questions.every(q => q.question.trim().length > 0);
      case 3: // Demographics - always valid
        return true;
      case 4: // Settings - always valid
        return true;
      case 5: // Preview - always valid
        return true;
      case 6: // Launch - always valid
        return true;
      default:
        return false;
    }
  }, [name, type, definition, isQuestionless]);

  // Why Next is blocked, so a disabled button is never unexplained. Mirrors isStepValid.
  const stepBlockedReason = useCallback((step: number): string | undefined => {
    if (step === 1) {
      if (!name.trim()) return 'Enter a name to continue';
      if (!type) return 'Choose a type to continue';
      return undefined;
    }
    if (step === 2 && !isQuestionless) {
      if (definition.questions.length === 0) return 'Add at least one question to continue';
      if (definition.questions.some(q => !q.question.trim())) return 'Every question needs text to continue';
    }
    return undefined;
  }, [name, type, definition, isQuestionless]);

  const isStepEditable = useCallback((step: number): boolean => {
    if (settingsOnlyMode) return step === 4 || step === 5;
    if (isLaunched) return step === 1 || step === 4 || step === 5;
    if (isQuestionless && step === 2) return false;
    return true;
  }, [settingsOnlyMode, isLaunched, isQuestionless]);

  const editableSteps = STEPS
    .map(s => s.number)
    .filter(n => isStepEditable(n));

  // Save progress without closing or showing toast. Returns the item id
  // (truthy) on success so callers can act on it without waiting for the
  // createdItemId state update to land.
  const saveProgress = async (): Promise<string | null> => {
    if (!name.trim() || !type) return null;
    try {
      setSaving(true);
      const result = await onSave(name, description, type, definition, false, undefined, createdItemId);
      if (result?.itemId && !createdItemId) {
        setCreatedItemId(result.itemId);
      }
      setLastSavedStep(currentStep);
      return result?.itemId || createdItemId || null;
    } catch (error) {
      console.error('Auto-save failed:', error);
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Whether the wizard has unsaved progress worth prompting about
  const hasSavableProgress = !!(name.trim() && type && !launched && currentStep > 0 && currentStep > lastSavedStep);

  const handleAttemptClose = useCallback(() => {
    if (hasSavableProgress) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  }, [hasSavableProgress, onClose]);

  useImperativeHandle(ref, () => ({ attemptClose: handleAttemptClose }), [handleAttemptClose]);

  // Navigation
  const handleNext = async () => {
    if (isStepValid(currentStep)) {
      markStepComplete(currentStep);
      if (currentStep >= 1 && name.trim() && type) {
        await saveProgress();
      }
      if (settingsOnlyMode) return;
      const idx = editableSteps.indexOf(currentStep);
      if (idx >= 0 && idx < editableSteps.length - 1) {
        setCurrentStep(editableSteps[idx + 1]);
      }
    }
  };

  const handleBack = () => {
    if (settingsOnlyMode) return;
    const idx = editableSteps.indexOf(currentStep);
    if (idx > 0) {
      setCurrentStep(editableSteps[idx - 1]);
    }
  };

  const handleStepClick = (step: number) => {
    if (!isStepEditable(step)) return;
    if (step <= 1 || completedSteps.has(1)) {
      setCurrentStep(step);
    }
  };

  const canNavigateToStep = useCallback((step: number): boolean => {
    if (!isStepEditable(step)) return false;
    return step <= 1 || completedSteps.has(1);
  }, [completedSteps, isStepEditable]);

  // Handle template selection -- preserve org branding when applying a template
  const handleTemplateSelect = (template: OutreachTemplate | null) => {
    setSelectedTemplate(template);
    if (template) {
      setType(template.type);
      setDefinition(prev => {
        const orgBranding = orgBrandingRef.current;
        const branding = orgBranding
          ? buildBrandingFromOrg(orgBranding, organizationLogoUrl, workspaceLogoUrl)
          : prev.branding;
        return {
          ...template.definition_json,
          branding
        };
      });
      if (!name) {
        setName(template.name);
      }
    }
    markStepComplete(0);
    setCurrentStep(1);
  };

  // Step 0 is the single "what are you creating" moment: a template OR a
  // from-scratch kind. Type is fixed here and shown as an inert chip in Step 1.
  const handleStartFromScratch = (scratchType: 'campaign' | 'survey' | 'poll' | 'message' | 'petition') => {
    setSelectedTemplate(null);
    setType(scratchType);
    if (scratchType === 'petition') {
      // A petition's signature is the demographics form — seed it enabled
      setDefinition(prev => ({
        ...prev,
        demographics: {
          enabled: true,
          required_fields: ['first_name', 'last_name', 'email'],
          optional_fields: ['district']
        }
      }));
    }
    markStepComplete(0);
    setCurrentStep(1);
  };

  const handlePublishChanges = async () => {
    const saved = await saveProgress();
    if (saved) {
      toast.success('Changes published successfully');
      onClose();
    } else {
      toast.error('Failed to publish changes');
    }
  };

  const handleSaveDraft = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for your outreach');
      return;
    }
    if (!type) {
      toast.error('Please select an outreach type');
      return;
    }

    const saved = await saveProgress();
    if (saved) {
      toast.success('Draft saved successfully');
      onClose();
    } else {
      toast.error('Failed to save draft');
    }
  };

  // Launch now
  const handleLaunchNow = async (dates?: { effective_start?: string; effective_end?: string }) => {
    // Validate Basic Info (step 1)
    if (!isStepValid(1)) {
      if (!name.trim()) {
        toast.error('Please enter a name for your outreach');
      } else if (!type) {
        toast.error('Please select an outreach type');
      }
      return;
    }
    
    // Validate Questions (step 2) - skip for questionless types
    if (!isQuestionless && !isStepValid(2)) {
      if (definition.questions.length === 0) {
        toast.error('Please add at least one question');
      } else if (definition.questions.some(q => !q.question.trim())) {
        toast.error('Please fill in all question text');
      }
      return;
    }

    // A petition's signature IS the demographics form — it must be enabled
    if (type === 'petition' && (!definition.demographics?.enabled ||
        (definition.demographics.required_fields || []).length === 0)) {
      toast.error('Petitions need demographics enabled with at least one required field — that is the signature form');
      return;
    }

    setLaunching(true);
    try {
      const result = await onSave(name, description, type, definition, true, dates, createdItemId);
      if (result) {
        setCreatedItemId(result.itemId);
        if (result.publicUrl) {
          setCollectorUrl(result.publicUrl);
        }
        setLaunched(true);
        markStepComplete(6);
      }
    } catch (error) {
      console.error('Failed to launch:', error);
      toast.error('Failed to launch outreach');
    } finally {
      setLaunching(false);
    }
  };

  const handleCreatePublicReport = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for your outreach');
      return;
    }
    if (!type) {
      toast.error('Please select an outreach type');
      return;
    }

    const savedId = await saveProgress();
    if (savedId) {
      setShowReportWizard(true);
    } else {
      toast.error('Failed to save draft');
    }
  };

  // Save as draft and mint a test link (Phase 2 test mode) so the real
  // public flow can be exercised before launch
  const handleLaunchTest = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for your outreach');
      return;
    }
    if (!type) {
      toast.error('Please select an outreach type');
      return;
    }

    setCreatingTestLink(true);
    try {
      const savedId = await saveProgress();
      if (!savedId) {
        toast.error('Failed to save draft');
        return;
      }
      const response = await api.post(`/outreach-unified/items/${savedId}/test-link`, {});
      const url = response.data.data?.public_url;
      if (url) {
        setTestLinkUrl(url);
      } else {
        toast.error('Failed to create test link');
      }
    } catch (error) {
      console.error('Failed to create test link:', error);
      toast.error('Failed to create test link');
    } finally {
      setCreatingTestLink(false);
    }
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <WizardStep0TemplateSelection
            templates={templates}
            selectedTemplate={selectedTemplate}
            onSelectTemplate={handleTemplateSelect}
            onStartFromScratch={handleStartFromScratch}
          />
        );
      case 1:
        return (
          <WizardStep1BasicInfo
            name={name}
            description={description}
            type={type}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            workspaceId={workspaceId}
            entityLinks={entityLinks}
            onEntityLinksChange={setEntityLinks}
            images={definition.images}
            onImagesChange={(images) => setDefinition(prev => ({ ...prev, images }))}
          />
        );
      case 2:
        return (
          <WizardStep2Questions
            questions={definition.questions}
            onQuestionsChange={(questions) => setDefinition(prev => ({ ...prev, questions }))}
          />
        );
      case 3:
        return (
          <WizardStep3Demographics
            definition={definition}
            onChange={setDefinition}
          />
        );
      case 4:
        return (
          <WizardStep4Settings
            definition={definition}
            onChange={setDefinition}
          />
        );
      case 5:
        return (
          <WizardStep5Preview
            name={name}
            description={description}
            type={type}
            definition={definition}
            onChange={setDefinition}
            organizationLogoUrl={organizationLogoUrl}
            workspaceLogoUrl={workspaceLogoUrl}
          />
        );
      case 6:
        return (
          <WizardStep6Launch
            itemId={createdItemId}
            collectorUrl={collectorUrl}
            onSaveDraft={handleSaveDraft}
            onLaunchNow={handleLaunchNow}
            onCreatePublicReport={handleCreatePublicReport}
            onLaunchTest={handleLaunchTest}
            testLinkUrl={testLinkUrl}
            creatingTestLink={creatingTestLink}
            saving={saving}
            launching={launching}
            launched={launched}
            shareResultsLink={!!definition.share_results_link}
          />
        );
      default:
        return null;
    }
  };

  const steps = STEPS
    .filter(step => {
      if (settingsOnlyMode) return step.number === 4 || step.number === 5;
      if (isLaunched) return step.number === 1 || step.number === 4 || step.number === 5;
      if (isEditing) return step.number !== 0;
      return true;
    })
    .map(step => ({
      ...step,
      completed: completedSteps.has(step.number)
    }));

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Step Indicator */}
        <div className="flex-shrink-0">
          <WizardStepIndicator
            steps={steps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
            canNavigateToStep={canNavigateToStep}
          />
        </div>

        {/* Edit restriction banner */}
        {settingsOnlyMode && (
          <div className="flex-shrink-0 mx-6 mt-4 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
            <Info className="w-4 h-4 flex-shrink-0" />
            This outreach has received responses. Only settings and design can be modified.
          </div>
        )}
        {isLaunched && !settingsOnlyMode && (
          <div className="flex-shrink-0 mx-6 mt-4 px-4 py-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg flex items-center gap-2 text-sm text-blue-800 dark:text-blue-300">
            <Info className="w-4 h-4 flex-shrink-0" />
            This outreach has been launched. Questions, demographics, and type cannot be changed.
          </div>
        )}

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {renderStep()}
        </div>

        {/* Navigation */}
        {!launched && currentStep > 0 && (
          <div className="flex-shrink-0">
            <WizardNavigation
              currentStep={settingsOnlyMode ? 1 : editableSteps.indexOf(currentStep) + 1}
              totalSteps={settingsOnlyMode ? 1 : editableSteps.length}
              canGoBack={!settingsOnlyMode && editableSteps.indexOf(currentStep) > 0}
              canGoNext={!settingsOnlyMode && editableSteps.indexOf(currentStep) < editableSteps.length - 1}
              onBack={handleBack}
              onNext={handleNext}
              onSaveDraft={isLaunched ? handlePublishChanges : handleSaveDraft}
              onLaunch={currentStep === 6 ? () => handleLaunchNow() : undefined}
              saving={saving}
              launching={launching}
              isStepValid={isStepValid(currentStep)}
              blockedReason={stepBlockedReason(currentStep)}
              // The Launch step presents Save Draft / Launch Test / Launch Now as cards;
              // repeating them in the bar left two of each on screen.
              hidePrimaryActions={currentStep === 6 && !isLaunched}
              saveDraftLabel={isLaunched ? 'Publish Changes' : undefined}
              savingLabel={isLaunched ? 'Publishing...' : undefined}
            />
          </div>
        )}
      </div>

      {showReportWizard && createdItemId && (
        <OutreachReportWizard
          itemId={createdItemId}
          itemName={name}
          existingView={null}
          initialDefaultPublic
          onClose={(reportSaved?: boolean) => {
            setShowReportWizard(false);
            if (reportSaved) {
              onClose();
            }
          }}
        />
      )}

      {showCloseConfirm && (
        <Modal
          isOpen={true}
          title="Unsaved changes"
          onClose={() => setShowCloseConfirm(false)}
          size="sm"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCloseConfirm(false);
                  onClose();
                }}
              >
                Discard
              </Button>
              <Button
                variant="primary"
                onClick={async () => {
                  setShowCloseConfirm(false);
                  await handleSaveDraft();
                }}
              >
                Save &amp; close
              </Button>
            </>
          }
        >
          <p className="text-text-secondary dark:text-text-dark-secondary">
            You have unsaved changes. Would you like to save your progress before closing?
          </p>
        </Modal>
      )}
    </>
  );
});

OutreachCreationWizard.displayName = 'OutreachCreationWizard';

export default OutreachCreationWizard;

